import { useSyncExternalStore } from 'react';

/**
 * Tiny reactive wrapper around `window.matchMedia` backed by
 * `useSyncExternalStore`.
 *
 * Returns whether the supplied CSS media query currently matches the
 * viewport, and re-renders the consumer as the match state changes. Used
 * primarily by `AdminLayout` (CON-DS-001) to collapse the sidebar below
 * the `md` (768px) breakpoint, but it is a general-purpose hook — any
 * consumer that needs a media-query-driven boolean can import it.
 *
 * Why `useSyncExternalStore` over `useEffect` + `useState`: it hands the
 * concurrent renderer the responsibility of subscribing/unsubscribing and
 * the getter re-reads on every render, so the returned value is always
 * synchronised with the external source even if `query` changes between
 * renders. It also avoids the "setState in effect" cascading-render
 * lint warning.
 *
 * SSR safe: the snapshot is `false` on the server (no window/matchMedia).
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = (onStoreChange: () => void): (() => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => {};
    }
    const mql = window.matchMedia(query);
    mql.addEventListener('change', onStoreChange);
    return () => mql.removeEventListener('change', onStoreChange);
  };

  const getSnapshot = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const getServerSnapshot = (): boolean => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
